/**
 * Dashboard dictionaries - how to add and name strings.
 *
 * Layout
 *   `dictionaries/<lang>/<namespace>.ts` default-exports a flat
 *   `Record<string, string>`. English (`en`) is the source of truth and is
 *   bundled eagerly; every other language is lazy-loaded per file. A new file
 *   is picked up automatically - there is no registry to update.
 *
 * Keys
 *   - Call sites prefix the namespace: `t('common.save')` reads key `save` of
 *     this file. Keys inside a file never repeat the namespace.
 *   - lower snake_case segments, dots to group: `errors.network`,
 *     `validation.too_long`. Name what the string *is*, not where it renders.
 *   - Backend error codes are used verbatim under `errors.`:
 *     `errors.PLAN_LIMIT_REACHED`, so `t(`languages.errors.${error.code}`)`
 *     works without a mapping table. Enum values likewise (`source.MANUAL`).
 *
 * Placeholders
 *   `{name}` is filled from `t(key, { name })`. Keep every placeholder in every
 *   language; reorder freely. Format numbers, money and dates with
 *   `formatters` from `useI18n()` before passing them in.
 *
 * Plurals
 *   Pass `count` and define CLDR forms: `items_one`, `items_other` (plus
 *   `_zero`, `_two`, `_few`, `_many` where a language needs them). `_other` is
 *   required whenever plural forms exist.
 *
 * Other languages
 *   Translate the same keys. A key missing from another language falls back to
 *   English at runtime, so a partial translation is safe to ship; a key missing
 *   from English too renders its humanised last segment and warns in dev.
 */
const common: Record<string, string> = {
  save: 'Save',
  save_changes: 'Save changes',
  saving: 'Saving…',
  saved: 'Saved',
  cancel: 'Cancel',
  delete: 'Delete',
  deleting: 'Deleting…',
  edit: 'Edit',
  create: 'Create',
  add: 'Add',
  remove: 'Remove',
  loading: 'Loading…',
  search: 'Search',
  search_placeholder: 'Search…',
  back: 'Back',
  next: 'Next',
  previous: 'Previous',
  close: 'Close',
  confirm: 'Confirm',
  yes: 'Yes',
  no: 'No',
  retry: 'Retry',
  refresh: 'Refresh',
  done: 'Done',
  apply: 'Apply',
  reset: 'Reset',
  clear: 'Clear',
  clear_all: 'Clear all',
  filters: 'Filters',
  all: 'All',
  none: 'None',
  actions: 'Actions',
  more: 'More',
  learn_more: 'Learn more',
  copy: 'Copy',
  copied: 'Copied',
  upload: 'Upload',
  download: 'Download',
  export: 'Export',
  import: 'Import',
  preview: 'Preview',
  view: 'View',
  settings: 'Settings',
  optional: 'Optional',
  required: 'Required',
  unsaved_changes: 'You have unsaved changes.',
  discard_changes: 'Discard changes',
  are_you_sure: 'Are you sure?',
  cannot_undo: 'This cannot be undone.',
  page_of: 'Page {page} of {pages}',
  items_one: '{count} item',
  items_other: '{count} items',
  selected_one: '{count} selected',
  selected_other: '{count} selected',
  upgrade_plan: 'Upgrade plan',

  'errors.generic': 'Something went wrong. Please try again.',
  'errors.network': 'We could not reach the server. Check your connection and try again.',
  'errors.forbidden': 'You do not have permission to do that.',
  'errors.not_found': 'We could not find what you were looking for.',
  'errors.plan_limit': 'You have reached the limit of your plan. Upgrade to add more.',
  'errors.rate_limited': 'Too many requests. Please wait a moment and try again.',
  'errors.session_expired': 'Your session has expired. Please sign in again.',
  'errors.load_failed': 'We could not load this. Please try again.',
  'errors.save_failed': 'We could not save your changes. Please try again.',
  'errors.PLAN_LIMIT_REACHED': 'You have reached the limit of your plan. Upgrade to add more.',
  'errors.RATE_LIMITED': 'Too many requests. Please wait a moment and try again.',
  'errors.FORBIDDEN': 'You do not have permission to do that.',
  'errors.NOT_FOUND': 'We could not find what you were looking for.',

  'validation.required': 'This field is required.',
  'validation.too_long': 'Use {max} characters or fewer.',
  'validation.too_short': 'Use at least {min} characters.',
  'validation.invalid_email': 'Enter a valid email address.',
  'validation.invalid_url': 'Enter a valid URL.',
  'validation.invalid_phone': 'Enter a valid phone number.',
  'validation.invalid_number': 'Enter a number.',
  'validation.min_value': 'Enter {min} or more.',
  'validation.max_value': 'Enter {max} or less.',
  'validation.select_one': 'Choose at least one option.',

  'status.active': 'Active',
  'status.inactive': 'Inactive',
  'status.enabled': 'Enabled',
  'status.disabled': 'Disabled',
  'status.draft': 'Draft',
  'status.published': 'Live',
  'status.pending': 'Pending',
  'status.running': 'Running',
  'status.completed': 'Completed',
  'status.failed': 'Failed',
  'status.cancelled': 'Cancelled',

  'empty.no_results': 'No results found.',
  'empty.nothing_here': 'Nothing here yet.',
  'empty.try_another_search': 'Try a different search or clear the filters.',
};

export default common;
